import { useEffect } from 'react'
import { NextPage, GetServerSidePropsContext } from 'next'
import { getApolloClient } from 'graphql/apolloClient'
import { getMetaProperties } from 'lib/condominium'
import { headerCtaEvent, openCondominiumPageEvent } from './events'
import { IconWhatsApp } from 'icons'
import logger from 'lib/logger'
import Template from 'templates/Default'
import {
  Address,
  Condominium,
  CondominiumDetails,
  GetCondominiumContextQuery,
  GetCondominiumContextQueryVariables,
  Listing,
  PointOfInterest,
  Tag,
} from 'graphql/types'
import GET_CONDOMINIUM_CONTEXT from 'graphql/queries/condominiumContext'
import PreFooter from 'pages/regioes-atendidas/regiao/components/Sections/PreFooter'
import HeroSection from 'pages/regioes-atendidas/components/Sections/Hero'
import AboutSection from 'pages/regioes-atendidas/components/Sections/AboutSection'
import PointsOfInterestSection from 'pages/regioes-atendidas/components/Sections/PointsOfInterest'

import Button from 'components/Button'
import DataSection from './components/Sections/Data'
import TagsSection from './components/Sections/Tags'
import RelatedSection from './components/Sections/Related'
import CondominiumsSection from './components/Sections/Condominiums'
import styles from '../Region.module.scss'

export type TProps = {
  address: Address
  condominium: CondominiumDetails
  relatedCondominiums: Array<Condominium>
  relatedListings: {
    listings: Array<Listing>
    total: number
  }
}

const CondominiumPage: NextPage<TProps> = ({
  condominium,
  relatedCondominiums,
  address,
  relatedListings,
}) => {
  useEffect(() => {
    if (condominium?.name) {
      openCondominiumPageEvent(condominium.name)
    }
  }, [condominium?.name])

  const prevPath = `${address?.stateSlug as string}/${
    address?.citySlug as string
  }/${address?.neighborhoodSlug as string}`
  const searchUrl = `/imoveis/${prevPath}/${condominium?.nameSlug as string}`

  if (!condominium) return null

  return (
    <Template
      head={getMetaProperties(condominium, address)}
      customClass={{
        main: styles.ecPageRegion,
      }}
      headerProps={{
        hideShadow: true,
        TrailingElement: (
          <Button
            size="small"
            theme="green"
            element="a"
            href="https://api.whatsapp.com/send?phone=551131976638&text=Quero%20falar%20com%20a%20EmCasa%20para%20tirar%20d%C3%BAvidas"
            target="_blank"
            Icon={{
              Component: IconWhatsApp,
              color: 'white',
              title: 'Ícone: WhatsApp',
            }}
            onClick={headerCtaEvent}
          >
            Fale conosco
          </Button>
        ),
      }}
    >
      <HeroSection
        address={address}
        condominium={{
          name: condominium?.name as string,
          nameSlug: condominium?.nameSlug as string,
        }}
        locationType="condominium"
        title={
          condominium.constructionYear
            ? `Construído em ${condominium?.constructionYear}`
            : ''
        }
      />
      <div className={styles.ecPageRegionContainer}>
        <AboutSection
          location="o condomínio"
          text={condominium?.information?.description}
        />
        <DataSection {...condominium} />
        <TagsSection tags={condominium.tags as Array<Tag>} />
        <PointsOfInterestSection
          locationType="condominium"
          pointsOfInterest={
            (condominium.pointsOfInterest as PointOfInterest[]) || []
          }
        />
        <RelatedSection
          condominiumName={condominium.name as string}
          listings={relatedListings.listings}
          total={relatedListings.total}
          searchUrl={searchUrl}
        />
        <AboutSection
          location={address?.neighborhood as string}
          text={address?.neighborhoodDescription}
          showMore={{
            text: `Quer conhecer mais sobre ${
              address?.neighborhood as string
            }? Clique aqui e saiba mais`,
            href: `/regioes-atendidas/${prevPath}`,
          }}
        />
        {relatedCondominiums?.length ? (
          <CondominiumsSection
            address={address}
            condominium={condominium.name as string}
            items={
              relatedCondominiums as Array<{
                name: string
                nameSlug: string
              }>
            }
          />
        ) : null}
        <PreFooter address={address} locationType="neighborhood" />
      </div>
    </Template>
  )
}

export const getServerSideProps = async ({
  query,
}: GetServerSidePropsContext) => {
  const state = query.state as string
  const city = query.city as string
  const neighborhood = query.neighborhood as string
  const condominium = query.condominium as string

  const apolloClient = getApolloClient()
  const { data, error } = (await apolloClient
    .query<GetCondominiumContextQuery, GetCondominiumContextQueryVariables>({
      query: GET_CONDOMINIUM_CONTEXT,
      variables: {
        stateSlug: state,
        citySlug: city,
        neighborhoodSlug: neighborhood,
        nameSlug: condominium,
        searchPagination: {
          from: 0,
          size: 3,
        },
        searchFilters: {
          statuses: ['active'],
          locationAddress: {
            statesSlug: [state],
            citiesSlug: [city],
            neighborhoodsSlugs: [neighborhood],
          },
        },
        condominium: condominium,
      },
    })
    .catch((e) => logger.capture(e))) || { data: {} }

  if (!data?.condominiumDetails || error) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      address: data.condominiumDetails.address,
      relatedCondominiums: data.condominiumDetails.condominiums,
      condominium: data.condominiumDetails,
      relatedListings: {
        listings: data.searchListings?.listings,
        total: data.searchListings?.totalCount,
      },
    },
  }
}
export default CondominiumPage
